/* Vertragsdaten: Laden und Speichern (Tabellen player_contracts und
 * player_contract_files, seit Stufe 3 Teil 2).
 *
 * Nur admin, board und data haben Zugriff (Datenbankregeln). Die App ruft
 * dieses Modul deshalb nur für diese Rollen auf; für staff und guest wird gar
 * nicht erst geladen.
 *
 * Fehler werden hier zentral behandelt: Details in die Konsole, im UI nur die
 * allgemeine Meldung (Schlüssel 'loadFailed' / 'saveFailed').
 */

import { supabase } from './client.js';
import { removeFile } from './storage.js';
import {
  contractToRow, contractFileToRow, isContractEmpty,
} from '../domain/contracts.js';

/* Spieler, für die es beim Laden schon eine Vertragszeile gab. Solange es keine
   gibt und das Formular leer ist, wird auch keine angelegt. */
const knownRows = new Set();

/** Alle Vertragszeilen und Dateizeilen: { contracts: Map<id, row>, files: Map<id, row[]> }. */
export async function loadContracts() {
  if (!supabase) return { contracts: new Map(), files: new Map() };
  const [c, f] = await Promise.all([
    supabase.from('player_contracts').select('*'),
    supabase.from('player_contract_files').select('*').order('created_at', { ascending: true }),
  ]);
  if (c.error || f.error) {
    console.error('[contracts] Laden fehlgeschlagen:', (c.error || f.error).message);
    throw new Error('loadFailed');
  }
  const contracts = new Map();
  for (const row of c.data || []) {
    contracts.set(row.player_id, row);
    knownRows.add(row.player_id);
  }
  const files = new Map();
  for (const row of f.data || []) {
    if (!files.has(row.player_id)) files.set(row.player_id, []);
    files.get(row.player_id).push(row);
  }
  return { contracts, files };
}

/** Pfade der Vertragsdateien eines Spielers (für das Löschen des Spielers). Wirft bei Fehler. */
export async function listContractFilePaths(playerId) {
  if (!supabase) throw new Error('notConnected');
  const { data, error } = await supabase.from('player_contract_files').select('path').eq('player_id', playerId);
  if (error) {
    console.error('[contracts] Laden fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }
  return (data || []).map((r) => r.path).filter(Boolean);
}

/**
 * Vertragszeile, neue Dateizeilen und entfernte Dateizeilen eines Spielers
 * schreiben. Reihenfolge: Zeile, neue Dateien, dann Entfernungen. Bei jedem
 * Fehler wird geworfen — der Aufrufer darf dann NICHT "gespeichert" melden.
 * Liefert die neue Dateiliste (ohne _new-Markierung, ohne entfernte).
 */
export async function saveContracts(player) {
  if (!supabase) throw new Error('notConnected');
  const id = player.id;
  const files = player.contractFiles || [];
  const removed = player.removedContractFiles || [];

  if (!isContractEmpty(player.contract) || knownRows.has(id)) {
    const { error } = await supabase
      .from('player_contracts')
      .upsert(contractToRow(id, player.contract), { onConflict: 'player_id' });
    if (error) {
      console.error('[contracts] Speichern fehlgeschlagen:', error.message);
      throw new Error('saveFailed');
    }
    knownRows.add(id);
  }

  const fresh = files.filter((f) => f._new);
  if (fresh.length) {
    const { error } = await supabase
      .from('player_contract_files')
      .insert(fresh.map((f) => contractFileToRow(id, f)));
    if (error) {
      console.error('[contracts] Dateien speichern fehlgeschlagen:', error.message);
      throw new Error('saveFailed');
    }
  }

  const persistedRemoved = removed.filter((f) => f.id && !f._new);
  if (persistedRemoved.length) {
    const { data, error } = await supabase
      .from('player_contract_files')
      .delete()
      .in('id', persistedRemoved.map((f) => f.id))
      .select('id');
    /* Die Datenbank lässt das Löschen nur admin zu und meldet sonst keinen
       Fehler, sondern "0 Zeilen". Das darf nicht als Erfolg durchgehen. */
    if (error || (data || []).length !== persistedRemoved.length) {
      console.error('[contracts] Dateizeilen löschen fehlgeschlagen:', error ? error.message : 'nicht alle gelöscht');
      throw new Error('saveFailed');
    }
  }
  /* Erst nach dem Zeilen-Löschen die Datei selbst — ohne Zeile ist sie ohnehin
     nicht mehr erreichbar; umgekehrt bliebe bei einem Fehler eine Zeile ohne Datei. */
  for (const f of removed) if (f.path) await removeFile(f.path);

  return files.map(({ _new, ...rest }) => rest);
}

